import { AspectRatio, cn, Icon, Text } from '@mntn/ui-components'
import { type HTMLAttributes, type ReactNode, useMemo } from 'react'

// Ratio steps with the card's own width: 6:5, then 5:4 from 500px, then 10:7 from 600px.
const landscapeAspectClasses = 'aspect-[6/5] @min-[500px]:aspect-[5/4] @min-[600px]:aspect-[10/7]'

export type Layout = 'landscape' | 'portrait' | 'landscape-wide' | 'landscape-tall' | 'fill'

export interface MediaCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Determines the card's orientation: 'landscape' or 'portrait' */
  layout: Layout
  /** React element to display as the card's main content (e.g., <img> or a group of elements) */
  media: ReactNode
  /** React element to display as an overlay at the bottom of the card */
  footer?: ReactNode
  /** Whether or not the content is in a draft state. When true, displays an unpublished overlay. */
  isDraft: boolean
  /** Optional callback function triggered when the card is clicked */
  onClick?: () => void
  /** CSS classes for the card frame — sizing, positioning, and corner radius (e.g. `rounded-md`); the inner content inherits the frame's radius. */
  className?: string
  /** Whether or not the card displays a bundle tag, displays on top left*/
  header?: ReactNode
  /** Width of the glass border in pixels (default: 1) */
  borderWidth?: number
}

/**
 * A component for displaying media content in either landscape or portrait orientation.
 * Adds an unpublished overlay for draft content, applies a hover scaling effect for
 * published content, and adds an overlay border.
 */
export const MediaCard = ({
  layout,
  media,
  footer,
  isDraft,
  className,
  onClick,
  header,
  borderWidth = 1,
  ...props
}: MediaCardProps) => {
  const mediaContent = useMemo(() => {
    return (
      <div className="h-full flex flex-col">
        {isDraft && <UnpublishedOverlay />}
        <div className="w-full h-full flex flex-col flex-1">{media}</div>
        {footer && <div className="absolute z-10 bottom-0 left-0 right-0">{footer}</div>}
        {header && <div className="absolute z-10 top-0 left-0 right-0">{header}</div>}
      </div>
    )
  }, [isDraft, media, footer, header])

  const layoutContent = useMemo(() => {
    switch (layout) {
      case 'landscape-wide':
        return <div className="w-full h-[480px] group relative">{mediaContent}</div>
      case 'landscape-tall':
        return <div className="w-full h-[560px] group relative">{mediaContent}</div>
      case 'fill':
        return <div className="w-full h-full group relative">{mediaContent}</div>
      // Not `AspectRatio`: it takes one fixed ratio, and this one steps with the card's width.
      case 'landscape':
        return <div className={cn('relative w-full group overflow-hidden', landscapeAspectClasses)}>{mediaContent}</div>
      case 'portrait':
        return (
          <AspectRatio ratio={4 / 5} className="w-full group overflow-hidden">
            {mediaContent}
          </AspectRatio>
        )
      default:
        return null
    }
  }, [layout, mediaContent])

  return (
    <div
      className={cn('flex flex-col border-glass', layout === 'fill' && 'h-full', className)}
      style={{ padding: `${borderWidth}px` }}
    >
      <div
        {...props}
        onClick={onClick}
        className={cn(
          'group relative w-full overflow-hidden rounded-[inherit]',
          layout === 'fill' && 'h-full',
          layout === 'landscape' && '@container',
          onClick && 'cursor-pointer',
        )}
        data-testid="media-card"
      >
        {layoutContent}
      </div>
    </div>
  )
}

const UnpublishedOverlay = () => {
  return (
    <div className="absolute h-full w-full bg-dark-bg/60 flex items-center justify-center z-10">
      <div className="relative bottom-6 flex flex-col items-center p-4 gap-2 rounded-s bg-dark-bg/50">
        <Icon name="CircleOff" variant="positionLeft" className="text-warning" />
        <Text variant="h5" className="text-header-inverse">
          UNPUBLISHED
        </Text>
      </div>
    </div>
  )
}
